import { deriveDotPhrasePlainText, sanitizeDotPhraseBodyHtml } from "@/dot-phrases";
import {
  isHtmlEmpty,
  normalizeEmptyHtml,
  plainTextToHtml,
  startsWithBlockTag,
} from "@/utils/rich-text";

/**
 * The lab results note as sanitized HTML, for the editor and the web views.
 *
 * @param storedNote - the note as stored: rich text, or plain text written before dotphrases.
 * @returns sanitized HTML.
 */
export const toLabResultsNoteHtml = (storedNote: string): string => {
  if (!startsWithBlockTag(storedNote)) return plainTextToHtml(storedNote);

  return sanitizeDotPhraseBodyHtml(storedNote);
};

/**
 * The lab results note as plain text, for the client chart export.
 *
 * @param storedNote - the note as stored: rich text, or plain text written before dotphrases.
 * @returns plain text with paragraph and line breaks kept as newlines.
 */
export const readLabResultsNoteText = (storedNote: string): string => {
  if (!startsWithBlockTag(storedNote)) return storedNote;

  return deriveDotPhrasePlainText(storedNote);
};

/**
 * Whether the stored note has no text to show.
 *
 * @param storedNote - the note as stored: rich text, or plain text written before dotphrases.
 * @returns true when the note is blank.
 */
export const isLabResultsNoteEmpty = (storedNote: string): boolean => {
  if (!startsWithBlockTag(storedNote)) return storedNote.trim() === "";

  return isHtmlEmpty(storedNote);
};

/**
 * The note in the form it is saved.
 *
 * @param draftNote - the note being reviewed: the editor's HTML, or a legacy plain-text note left unedited.
 * @returns sanitized HTML, or an empty string when the note has no text.
 */
export const toStoredLabResultsNote = (draftNote: string): string =>
  normalizeEmptyHtml(toLabResultsNoteHtml(draftNote));
